import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Sparkles, Share2, Copy } from "lucide-react";
import { ChatWidget } from "@/components/ChatWidget";
import { ProductReviews } from "@/components/ProductReviews";
import { LegalFooter } from "@/components/LegalFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const chf = (n) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n || 0);

export default function ShopProductPage() {
  const { handle } = useParams();
  const [searchParams] = useSearchParams();
  const { lang } = useLang();
  const [p, setP] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [abPrice, setAbPrice] = useState(null); // {experiment_id, arm, price_chf}

  useEffect(() => {
    setLoading(true);
    shopApi
      .get(`/shop/products/${handle}?lang=${lang}`)
      .then((r) => setP(r.data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
    if (searchParams.get("cancelled") === "1") toast.info(t(lang, "shop_checkout_cancelled"));
  }, [handle, lang, searchParams]);

  // Persistent anonymous session — used by A/B assignment (never leaks PII).
  const getSessionId = () => {
    try {
      let sid = localStorage.getItem("sh_session_id");
      if (!sid) {
        sid = `sh_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("sh_session_id", sid);
      }
      return sid;
    } catch {
      return `sh_anon_${Math.random().toString(36).slice(2, 10)}`;
    }
  };

  // Query the A/B harness once the product is loaded.
  useEffect(() => {
    if (!p?.shopify_id) return;
    shopApi
      .post("/experiments/assign", {
        shopify_id: p.shopify_id,
        session_id: getSessionId(),
      })
      .then((r) => {
        if (r.data?.experiment_id && r.data.price_chf != null) {
          setAbPrice(r.data);
        } else {
          setAbPrice(null);
        }
      })
      .catch(() => setAbPrice(null));
  }, [p?.shopify_id]);

  const effectivePrice = abPrice?.price_chf ?? p?.price_chf;

  // Inject structured data + Open Graph tags for Google/Facebook/WhatsApp/Pinterest
  useEffect(() => {
    if (!p) return;
    const url = `${window.location.origin}/shop/${handle}`;
    const title = p.title || "EMRKT.CH";
    const description = (p.short_description || p.long_description || title).slice(0, 200);
    const image = p.image_url || p.images?.[0]?.src || "";

    // Meta / OG tags
    document.title = `${title} — EMRKT.CH`;
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag || "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => k !== "tag" && el.setAttribute(k, v));
    };
    upsert('meta[name="description"]', { name: "description", content: description });
    upsert('meta[property="og:type"]', { property: "og:type", content: "product" });
    upsert('meta[property="og:title"]', { property: "og:title", content: title });
    upsert('meta[property="og:description"]', { property: "og:description", content: description });
    upsert('meta[property="og:image"]', { property: "og:image", content: image });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    // JSON-LD structured data (Product)
    let ld = document.getElementById("shop-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "shop-jsonld";
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Product",
      name: title,
      description,
      image: image ? [image] : [],
      sku: String(p.shopify_id || ""),
      brand: { "@type": "Brand", name: p.vendor || "EMRKT.CH" },
      offers: {
        "@type": "Offer",
        priceCurrency: "CHF",
        price: (effectivePrice || p.price_chf || 0).toFixed(2),
        availability: "https://schema.org/InStock",
        url,
      },
    });

    return () => {
      // Reset on unmount
      const ldEl = document.getElementById("shop-jsonld");
      if (ldEl) ldEl.remove();
    };
  }, [p, handle, effectivePrice]);

  const shareText = p ? `${p.title} — CHF ${(effectivePrice || 0).toFixed(2)} @ EMRKT.CH` : "";
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/shop/${handle}` : "";
  const share = (channel) => {
    const enc = encodeURIComponent;
    const map = {
      whatsapp: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(p?.image_url || p?.images?.[0]?.src || "")}&description=${enc(shareText)}`,
      x: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`,
    };
    window.open(map[channel], "_blank", "noopener,noreferrer,width=600,height=600");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await shopApi.post("/shop/checkout", {
        handle,
        quantity: qty,
        lang,
        origin_url: window.location.origin,
        visitor_session_id: getSessionId(),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "shop_checkout_failed"));
      setBusy(false);
    }
  };

  if (loading) return <PageWrap><div className="text-center py-24 font-mono text-xs text-white/40">{t(lang, "shop_loading")}</div></PageWrap>;
  if (!p) return <PageWrap><div className="text-center py-24 font-mono text-xs text-white/40">{t(lang, "shop_not_found")}</div></PageWrap>;

  return (
    <PageWrap>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="shop-product-page">
        <Link to="/shop" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm transition-colors duration-200 mb-6" data-testid="shop-back-btn">
          <ArrowLeft size={14} /> {t(lang, "shop_back")}
        </Link>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Gallery */}
          <div className="space-y-3">
            <div className="hq-card aspect-square overflow-hidden">
              {p.images?.[selectedImg]?.src && (
                <img src={p.images[selectedImg].src} alt={p.title} className="w-full h-full object-cover" />
              )}
            </div>
            {p.images?.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {p.images.slice(0, 5).map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImg(i)}
                    className={`aspect-square border overflow-hidden transition-colors duration-200 ${
                      i === selectedImg ? "border-[color:var(--hq-cyan)]" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img src={img.src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {p.ai_enriched && (
              <div className="inline-flex items-center gap-2 border border-[color:var(--hq-cyan)]/30 bg-[color:var(--hq-cyan)]/5 px-3 py-1 rounded-full mb-4">
                <Sparkles size={10} className="text-[color:var(--hq-cyan)]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--hq-cyan)]">
                  {t(lang, "shop_ai_enriched")}
                </span>
              </div>
            )}
            <h1 data-testid="shop-product-title" className="font-display font-light text-3xl md:text-4xl tracking-tight leading-tight">
              {p.title}
            </h1>
            <div className="mt-4 flex items-baseline gap-3">
              <span data-testid="shop-product-price" className="font-mono text-3xl">
                {chf(effectivePrice)}
              </span>
              {abPrice && p.price_chf !== effectivePrice && (
                <span className="font-mono text-sm text-white/40 line-through">
                  {chf(p.price_chf)}
                </span>
              )}
              {!abPrice && p.ai_priced && p.current_price_chf && p.current_price_chf !== p.price_chf && (
                <span className="font-mono text-sm text-white/40 line-through">{chf(p.current_price_chf)}</span>
              )}
            </div>

            <p className="mt-6 text-white/70 leading-relaxed whitespace-pre-line">{p.long_description || p.short_description}</p>

            {p.tags?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {p.tags.map((tag) => (
                  <span key={tag} className="font-mono text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 flex items-center gap-3">
              <div className="flex items-center border border-white/15 rounded">
                <button data-testid="shop-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-11 hover:bg-white/5 transition-colors duration-200">−</button>
                <div className="w-10 text-center font-mono">{qty}</div>
                <button data-testid="shop-qty-plus" onClick={() => setQty((q) => Math.min(50, q + 1))} className="w-9 h-11 hover:bg-white/5 transition-colors duration-200">+</button>
              </div>
              <button
                data-testid="shop-buy-btn"
                onClick={buy}
                disabled={busy}
                className="hq-btn hq-btn-primary px-6 py-3 flex-1 disabled:opacity-50"
              >
                <ShoppingCart size={14} />
                {busy ? t(lang, "shop_processing") : t(lang, "shop_buy_now")}
              </button>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">
              {t(lang, "shop_secure_checkout")}
            </p>

            {/* Share row — free organic reach */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="flex items-center gap-2 mb-2 text-white/60">
                <Share2 size={13} />
                <span className="font-mono text-[10px] uppercase tracking-widest">Share this</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => share("whatsapp")} data-testid="share-whatsapp"
                        className="hq-btn hq-btn-ghost text-xs">WhatsApp</button>
                <button onClick={() => share("facebook")} data-testid="share-facebook"
                        className="hq-btn hq-btn-ghost text-xs">Facebook</button>
                <button onClick={() => share("pinterest")} data-testid="share-pinterest"
                        className="hq-btn hq-btn-ghost text-xs">Pinterest</button>
                <button onClick={() => share("x")} data-testid="share-x"
                        className="hq-btn hq-btn-ghost text-xs">X</button>
                <button onClick={copyLink} data-testid="share-copy"
                        className="hq-btn hq-btn-ghost text-xs inline-flex items-center gap-1">
                  <Copy size={10} /> Copy link
                </button>
              </div>
            </div>
          </div>
        </div>

        <ProductReviews handle={handle} />
      </div>
    </PageWrap>
  );
}

function PageWrap({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/shop" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-[color:var(--hq-cyan)] flex items-center justify-center">
              <span className="font-mono text-[10px] text-[color:var(--hq-cyan)]">SH</span>
            </div>
            <div className="font-display font-medium text-sm">EMRKT.CH</div>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>
      {children}
      <LegalFooter />
      <ChatWidget handle={typeof window !== 'undefined' ? window.location.pathname.split('/shop/')[1] : undefined} />
    </div>
  );
}
